import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import * as STATIC from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  brand: {
    name: STATIC.BRAND.name,
    tagline: STATIC.BRAND.tagline,
    whatsappNumber: STATIC.BRAND.whatsappNumber,
    whatsappMessage: STATIC.BRAND.whatsappMessage,
    whatsappCommunity: "",
    email: STATIC.BRAND.email,
    city: STATIC.BRAND.city,
    instagram: STATIC.BRAND.socials.instagram,
    youtube: STATIC.BRAND.socials.youtube,
    facebook: STATIC.BRAND.socials.facebook,
  },
  hero: {
    eyebrow: "Ancient Geomancy · Brahma Satya · Kalpataru",
    headlineLine1: "Manifest Your Desires",
    headlineLine2: "Through Ancient Geomantic Wisdom",
    subtitle:
      "Discover the transformative power of sacred geomantic symbols, spiritual siddhis and divine manifestation practices — designed for the modern seeker.",
    primaryCtaText: "Book a Consultation",
    secondaryCtaText: "Explore the Process",
    image: STATIC.IMG.hero,
  },
  highlight: {
    visible: true,
    kind: "Thought of the Day",
    eyebrow: "Thought of the Day",
    title: "Silence is the first mantra.",
    body: "Before any invocation, before any yantra — sit for one full minute in stillness. That is where the work begins.",
    image: STATIC.IMG.meditation,
    ctaText: "",
    ctaLink: "",
  },
  stats: [
    { n: "1000+", label: "Yrs of lineage" },
    { n: "480+", label: "Souls guided" },
    { n: "96%", label: "Tangible shift" },
    { n: "14", label: "Countries reached" },
  ],
  whatIs: {
    eyebrow: "What is Geomantic Kalpataru",
    title: "A living bridge between ancient wisdom and modern desire.",
    body1: "Geomancy is a sacred science of reading the energetic architecture of reality through symbols and figures. Kalpataru is the mythic wish-fulfilling tree of the cosmos — a precise blueprint for how intention becomes form.",
    body2: "Together they form a discipline of manifestation: not affirmation, not wishful thinking, but a structured spiritual technology that has shaped temples, kings and seekers for millennia.",
    image: STATIC.IMG.sacredGeometry,
    quote: "The symbol does not represent the truth — it is the truth, made visible.",
  },
  brahmaSatyaHome: {
    eyebrow: "Brahma Satya",
    title: "The energy stream behind the work",
    body: "Brahma Satya is the living tradition that holds Geomantic Kalpataru. Two sacred currents flow through it — each transforming a different layer of the seeker's being.",
    items: [
      { t: "Reiki Riddhi", d: "An evolved Reiki stream tuned for prosperity, decisive opportunity and right relationship." },
      { t: "Spiritual Healing Siddhi", d: "Deep subtle-body work that releases karmic residue across body, mind and lineage." },
    ],
    ctaText: "Explore Brahma Satya",
  },
  founder: {
    eyebrow: "A Message from the Founder",
    title: "I do not teach manifestation. I walk it with you.",
    body1: "For over two decades the founder of Geomantic Kalpataru has held the Brahma Satya stream — initiated into geomantic figures, Reiki Riddhi and the spiritual healing siddhi by lineage holders across India.",
    body2: "The work is private, personal and exacting. Every seeker is held individually. Every yantra is hand-crafted. Every transformation is witnessed.",
    image: STATIC.IMG.founder,
    ctaText: "The Founder's Journey",
    badge: "The Founder",
  },
  sections: {
    howItWorksTitle: "Five sacred steps from longing to lived reality.",
    howItWorksEyebrow: "The Process",
    benefitsTitle: "Six domains that quietly, decisively shift.",
    benefitsEyebrow: "What Transforms",
    testimonialsTitle: "Lives the Kalpataru has touched.",
    testimonialsEyebrow: "Success Stories",
    faqTitle: "Before you begin.",
    faqEyebrow: "Questions, answered",
    contactTitle: "Speak with us — the path begins with a conversation.",
    contactEyebrow: "Begin Your Journey",
    contactBody: "The founder personally responds to every initial enquiry. Reach out on WhatsApp for the fastest reply, or write to us by email.",
  },
  servicesPage: {
    eyebrow: "Our Services",
    title: "Four 'sacred instruments'. One transformation.",
    subtitle: "Each service stands on its own — and each is more powerful when woven into the full Geomantic Kalpataru journey.",
    heroImage: STATIC.IMG.temple,
    processEyebrow: "The Process",
    processTitle: "Five sacred steps — applied across every service.",
    benefitsEyebrow: "Unique Benefits",
    benefitsTitle: "Six domains that quietly, decisively shift.",
    ctaEyebrow: "Begin",
    ctaTitle: "Speak with us. The path begins with a 'conversation'.",
    ctaButton: "Book a Consultation",
  },
  products: STATIC.PRODUCTS.map((p) => ({ ...p, image: STATIC.IMG[p.image] || p.image, priceInr: 100 })),
  podcasts: STATIC.PODCASTS.map((p) => ({ ...p, image: STATIC.IMG[p.image] || p.image })),
  testimonials: STATIC.TESTIMONIALS.map((t) => ({ ...t, avatar: STATIC.IMG[t.avatar] || t.avatar })),
  faqs: STATIC.FAQS,
  benefits: STATIC.BENEFITS,
  howItWorks: STATIC.HOW_IT_WORKS,
  services: [
    { id: "kalpataru", label: "Geomantic Kalpataru", eyebrow: "Manifestation System", title: "The art of turning desire into form.", image: STATIC.IMG.mandala, intro: "Geomantic Kalpataru is a private engagement: your soul-signature is mapped into geomantic figures, a yantra is encoded for you, and a guide walks you through ninety days of disciplined practice.", points: ["Personalised geomantic mapping", "Bespoke consecrated yantra", "Ninety-day guided practice", "Private channel with your guide"] },
  ],
};

const ContentContext = createContext({ content: DEFAULTS, loading: true, refresh: () => {} });

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cms/content`);
      setContent({ ...DEFAULTS, ...data });
    } catch (e) {
      // keep defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <ContentContext.Provider value={{ content, loading, refresh }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);
